import React from 'react';
import { X, Package, DollarSign, Calendar, User } from 'lucide-react';
import { calculateOrderItemTotal, formatPrice, getCurrency, getPriceLabel } from '../../utils/pricingCalculations';

const RecurringOrderDetailModal = ({ 
  isOpen, 
  onClose, 
  order = null,
  productos = []
}) => {
  if (!isOpen || !order) return null;

  const calculateLineTotal = (detalle) => {
    const producto = detalle.productos || productos.find(p => p.id === detalle.producto_id);
    if (!producto) return 0;
    
    try {
      return calculateOrderItemTotal(producto, order.tipo, detalle.cantidad_cajas);
    } catch (error) {
      console.error('Error calculating line total:', error);
      return 0;
    }
  };

  const calculateOrderTotal = () => {
    return (order.detalles || []).reduce((sum, detalle) => {
      return sum + calculateLineTotal(detalle);
    }, 0);
  };

  const currency = getCurrency(order.tipo);
  const priceLabel = getPriceLabel(order.tipo);

  const getTotalCajas = () => {
    return (order.detalles || []).reduce((sum, detalle) => {
      return sum + (parseInt(detalle.cantidad_cajas) || 0);
    }, 0);
  };

  const getTypeBadgeClass = (tipo) => {
    return tipo === 'Exportación' ? 'badge badge-primary' : 'badge badge-success';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Detalle del Pedido Recurrente</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="modal-body">
          {/* Información del Cliente */}
          <div className="detail-section">
            <h3 className="detail-section-title">
              <User size={20} />
              Información del Cliente
            </h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Cliente:</span>
                <span className="detail-value">{order.clientes?.nombre || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Código:</span>
                <span className="detail-value">{order.clientes?.codigo_cliente || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">País:</span>
                <span className="detail-value">{order.clientes?.pais || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Ciudad:</span>
                <span className="detail-value">{order.clientes?.ciudad || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Email:</span>
                <span className="detail-value">{order.clientes?.email_contacto || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Tipo:</span>
                <span className={getTypeBadgeClass(order.tipo)}>{order.tipo}</span>
              </div>
            </div>
          </div>

          {/* Información del Pedido */}
          <div className="detail-section">
            <h3 className="detail-section-title">
              <Calendar size={20} />
              Información del Pedido Recurrente
            </h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Temporada:</span>
                <span className="detail-value">{order.temporada}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Semana Inicio:</span>
                <span className="detail-value">{order.semana_inicio}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Semana Fin:</span>
                <span className="detail-value">{order.semana_fin}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Duración:</span>
                <span className="detail-value">
                  {order.semana_fin - order.semana_inicio + 1} semanas
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Fecha Creación:</span>
                <span className="detail-value">
                  {order.fecha_creacion ? new Date(order.fecha_creacion).toLocaleDateString('es-CL') : 'N/A'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Última Modificación:</span>
                <span className="detail-value">
                  {order.fecha_modificacion ? new Date(order.fecha_modificacion).toLocaleDateString('es-CL') : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Productos */}
          <div className="detail-section">
            <h3 className="detail-section-title">
              <Package size={20} />
              Productos
            </h3>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Producto</th>
                    <th>Código</th>
                    <th>Cajas</th>
                    <th>{priceLabel}</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(order.detalles || []).map((detalle, index) => {
                    const producto = detalle.productos || productos.find(p => p.id === detalle.producto_id);
                    return (
                      <tr key={detalle.id || index}>
                        <td>{index + 1}</td>
                        <td>
                          <div className="font-medium">{producto?.nombre || 'N/A'}</div>
                          <div className="text-sm text-gray-500">{producto?.category || ''}</div>
                        </td>
                        <td>{producto?.code || 'N/A'}</td>
                        <td>{detalle.cantidad_cajas}</td>
                        <td>
                          {order.tipo === 'Exportación' 
                            ? formatPrice(producto?.precio_fob_mia || 0, 'USD')
                            : formatPrice(producto?.precio_unitario || 0, 'CLP')
                          }
                        </td>
                        <td className="font-medium">{formatPrice(calculateLineTotal(detalle), currency)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="font-bold">
                    <td colSpan="3" className="text-right">Total:</td>
                    <td>{getTotalCajas()}</td>
                    <td></td>
                    <td>{formatPrice(calculateOrderTotal(), currency)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Resumen */}
          <div className="detail-section">
            <h3 className="detail-section-title">
              <DollarSign size={20} />
              Resumen
            </h3>
            <div className="summary-cards">
              <div className="summary-card">
                <div className="summary-card-label">Total Cajas por Semana</div>
                <div className="summary-card-value">{getTotalCajas()}</div>
              </div>
              <div className="summary-card">
                <div className="summary-card-label">Valor por Semana ({currency})</div>
                <div className="summary-card-value">{formatPrice(calculateOrderTotal(), currency)}</div>
              </div>
              <div className="summary-card">
                <div className="summary-card-label">Total Semanas</div>
                <div className="summary-card-value">{order.semana_fin - order.semana_inicio + 1}</div>
              </div>
              <div className="summary-card highlight">
                <div className="summary-card-label">Valor Total Estimado ({currency})</div>
                <div className="summary-card-value">
                  {formatPrice(calculateOrderTotal() * (order.semana_fin - order.semana_inicio + 1), currency)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>
            Cerrar
          </button>
        </div>

        <style jsx>{`
          .detail-section {
            margin-bottom: var(--spacing-6);
            padding-bottom: var(--spacing-6);
            border-bottom: 1px solid var(--gray-200);
          }

          .detail-section:last-child {
            border-bottom: none;
            margin-bottom: 0;
            padding-bottom: 0;
          }

          .detail-section-title {
            display: flex;
            align-items: center;
            gap: var(--spacing-2);
            font-size: var(--font-size-lg);
            font-weight: 600;
            color: var(--gray-900);
            margin-bottom: var(--spacing-4);
          }

          .detail-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: var(--spacing-4);
          }

          .detail-item {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-1);
          }

          .detail-label {
            font-size: var(--font-size-sm);
            color: var(--gray-600);
            font-weight: 500;
          }

          .detail-value {
            font-size: var(--font-size-base);
            color: var(--gray-900);
            font-weight: 400;
          }

          .summary-cards {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: var(--spacing-4);
          }

          .summary-card {
            padding: var(--spacing-4);
            background-color: var(--gray-50);
            border-radius: var(--radius-lg);
            text-align: center;
          }

          .summary-card.highlight {
            background: linear-gradient(135deg, var(--primary-green) 0%, var(--secondary-green) 100%);
            color: white;
          }

          .summary-card-label {
            font-size: var(--font-size-sm);
            color: var(--gray-600);
            margin-bottom: var(--spacing-2);
          }

          .summary-card.highlight .summary-card-label {
            color: rgba(255, 255, 255, 0.9);
          }

          .summary-card-value {
            font-size: var(--font-size-2xl);
            font-weight: 700;
            color: var(--gray-900);
          }

          .summary-card.highlight .summary-card-value {
            color: white;
          }

          @media (max-width: 768px) {
            .detail-grid {
              grid-template-columns: 1fr;
            }

            .summary-cards {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default RecurringOrderDetailModal;
